import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";

import Heading from "../../../shared/ui/Typography/Heading";
import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { fetchCommunications } from "../api/communications";
import { messageFromError } from "../../../shared/api/drfErrors";

import ClientLogCommunicationModal from "./ClientLogCommunicationModal";

// ClientActivitySection — subordinate hydration boundary inside
// /clients/:clientId (Phase R4) + T-3 cross-trigger modal owner
// (Phase S6).
//
// Owns:
//   * its own /communications/ fetch
//   * its own load/error/refresh state
//   * an inline "Оновити" button local to the section header
//   * the "Залогувати взаємодію" modal lifecycle (open / close /
//     refetch on close). The slice, the modal, and the post-success
//     freshness all stay here.
//
// Does NOT own:
//   * client core (parent owns it)
//   * route validity — section failure does NOT take the route down
//   * any coordination with the contacts section or the parent
//
// Load lifecycle is duplicated inline from ClientContactsSection by
// design — per CLAUDE.md anti-abstraction rules. No useSectionLoader,
// no shared async hook. Sections stay "explicit, local, dumb,
// disposable".
//
// T-3 bridge (Phase S6 — architecture-freeze-r8.md §4 T-3 + I2
// §"R7 §6.4" proposal):
//
//   exposes exactly ONE imperative method via forwardRef +
//   useImperativeHandle:
//
//       openCommunicationModal()  — void, no args, no async, no
//                                   return payload, fire-and-forget.
//
//   The parent ([[ClientDetailScreenContent]]) holds a ref to this
//   section and may call this method from its header's "Залогувати
//   дзвінок" button. The parent observes nothing back: it does not
//   know whether the modal opened, whether the form was submitted,
//   whether a communication was saved, or whether the section
//   refetched. The parent is presentation-alias only.
//
//   Bridge narrowness (A-1 §6.4 audit):
//     (1) one method per ref               — only openCommunicationModal
//     (2) void return                       — returns undefined
//     (3) no async awaited from caller      — handler does not await
//     (4) no cross-section invocation       — parent calls one ref only
//     (5) no registry shape                 — single named method
//     (6) deletable                         — the section ALSO renders
//         its own "+ Залогувати" trigger; the parent's alias is purely
//         additive. Removing the bridge does not remove the entry
//         point.

const TYPE_LABELS = {
  call: "Дзвінок",
  sms: "SMS",
  email: "Email",
  message: "Повідомлення",
  meeting: "Зустріч",
  note: "Нотатка",
  other: "Інше",
};

const TYPE_ICONS = {
  call: "fa-solid fa-phone",
  sms: "fa-solid fa-comment-sms",
  email: "fa-solid fa-envelope",
  message: "fa-solid fa-comment",
  meeting: "fa-solid fa-handshake",
  note: "fa-solid fa-note-sticky",
  other: "fa-solid fa-circle-info",
};

const ICON_VARIANTS = {
  call: "call",
  sms: "message",
  email: "email",
  message: "message",
};

const DIRECTION_LABELS = {
  in: "Вхідне",
  out: "Вихідне",
  inbound: "Вхідне",
  outbound: "Вихідне",
};

const DIRECTION_VARIANT = {
  in: "in",
  inbound: "in",
  out: "out",
  outbound: "out",
};

const PREVIEW_LIMIT = 25;

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mo}-${dd} ${hh}:${mm}`;
};

const unwrapListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

function ClientActivitySection({ clientId }, ref) {
  const [communications, setCommunications] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  // Section-owned T-3 modal state (R8 §4 T-3). Local useState only —
  // the parent ClientDetailScreenContent is not informed when this
  // toggles; the parent only knows how to ASK us to open it.
  const [logOpen, setLogOpen] = useState(false);

  // T-3 narrow bridge. ONE method. void. fire-and-forget.
  // See header docstring for the §6.4 axis-by-axis justification.
  useImperativeHandle(
    ref,
    () => ({
      openCommunicationModal() {
        setLogOpen(true);
      },
    }),
    []
  );

  useEffect(() => {
    if (clientId == null) return undefined;

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchCommunications(clientId)
      .then((data) => {
        if (cancelled) return;
        setCommunications(unwrapListPayload(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleReload = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await fetchCommunications(clientId);
      setCommunications(unwrapListPayload(data));
      setLoadState("loaded");
    } catch (err) {
      setRefreshError(messageFromError(err));
    } finally {
      setRefreshing(false);
    }
  };

  const list = communications;
  const previewRows = list.slice(0, PREVIEW_LIMIT);
  const remaining = Math.max(list.length - PREVIEW_LIMIT, 0);

  return (
    <section
      className="cd-section feature-client-detail__section--activity"
      aria-labelledby="client-detail-activity-heading"
    >
      <header className="cd-section__head">
        <Heading
          id="client-detail-activity-heading"
          level={2}
          variant="section"
          icon="fa-solid fa-clock-rotate-left"
        >
          Журнал взаємодій
        </Heading>
        <div className="feature-client-detail__section-actions">
          <button
            type="button"
            className="cd-add-btn"
            onClick={() => setLogOpen(true)}
          >
            + Залогувати
          </button>
          <Link
            to={`/clients/${clientId}/activity`}
            className="feature-client-detail__section-link"
          >
            Перегляд журналу
          </Link>
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing || loadState === "loading"}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити журнал взаємодій"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-client-detail__section-state">
          <Spinner size="md" label="Завантаження активності" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-client-detail__section-state">
          <Text as="p" size="sm" tone="danger">
            {loadError || "Не вдалося завантажити активність."}
          </Text>
        </div>
      ) : list.length === 0 ? (
        <Text as="p" size="sm" tone="secondary">
          Активність по клієнту ще не зафіксована.
        </Text>
      ) : (
        <>
          <ul className="comm-timeline">
            {previewRows.map((row) => {
              const typeLabel = TYPE_LABELS[row.type] || row.type || "—";
              const directionLabel = DIRECTION_LABELS[row.direction] || null;
              const directionVariant = DIRECTION_VARIANT[row.direction] || null;
              const iconClass = TYPE_ICONS[row.type] || TYPE_ICONS.other;
              const iconVariant = ICON_VARIANTS[row.type];
              const iconClassName = iconVariant
                ? `comm-item__icon comm-item__icon--${iconVariant}`
                : "comm-item__icon";
              const itemClassName = directionVariant
                ? `comm-item comm-item--${directionVariant}`
                : "comm-item";
              return (
                <li key={row.id} className={itemClassName}>
                  <div className={iconClassName} aria-hidden="true">
                    <i className={iconClass} />
                  </div>
                  <div className="comm-item__body">
                    <header className="comm-item__head">
                      <span className="comm-item__type">{typeLabel}</span>
                      {directionLabel ? (
                        <span
                          className={
                            directionVariant
                              ? `comm-item__direction comm-item__direction--${directionVariant}`
                              : "comm-item__direction"
                          }
                        >
                          <i
                            className={
                              directionVariant === "in"
                                ? "fa-solid fa-arrow-left"
                                : "fa-solid fa-arrow-right"
                            }
                            aria-hidden="true"
                          />
                          <span>{directionLabel}</span>
                        </span>
                      ) : null}
                      {row.created_at ? (
                        <time>{formatDateTime(row.created_at)}</time>
                      ) : null}
                    </header>
                    {row.content ? <p>{row.content}</p> : null}
                  </div>
                </li>
              );
            })}
          </ul>
          {remaining > 0 ? (
            <div className="feature-client-detail__list-more">
              <Link
                to={`/clients/${clientId}/activity`}
                className="feature-client-detail__section-link"
              >
                <span>Переглянути всі ({list.length})</span>
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
          ) : null}
        </>
      )}

      <ClientLogCommunicationModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        clientId={clientId}
        onSaved={() => {
          setLogOpen(false);
          handleReload();
        }}
      />
    </section>
  );
}

export default forwardRef(ClientActivitySection);
